import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reportsService } from '../../../services/supabaseService';

const ReportsTab = ({ language }) => {
  const [period, setPeriod] = useState('month');
  const [analytics, setAnalytics] = useState([]);
  const [topCountries, setTopCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReports();
  }, [period]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const [analyticsData, countriesData] = await Promise.all([
        reportsService?.getQueryAnalytics(period),
        reportsService?.getTopCountriesByInterest()
      ]);
      setAnalytics(analyticsData);
      setTopCountries(countriesData);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const periodOptions = [
    { value: 'week', label: language === 'en' ? 'Weekly' : '每周' },
    { value: 'month', label: language === 'en' ? 'Monthly' : '每月' },
    { value: 'year', label: language === 'en' ? 'Yearly' : '每年' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-semibold text-foreground">
          {language === 'en' ? 'Analytics Reports' : '分析报告'}
        </h2>
        <div className="flex gap-2">
          {periodOptions?.map((option) => (
            <button
              key={option?.value}
              onClick={() => setPeriod(option?.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                period === option?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              {option?.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Top Countries by Interest */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="TrendingUp" size={20} className="text-primary" />
              {language === 'en' ? 'Most Requested Countries' : '最受欢迎的国家'}
            </h3>
            <div className="space-y-3">
              {topCountries?.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold">
                      {index + 1}
                    </div>
                    <span className="font-medium text-foreground">{item?.country}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {item?.count} {language === 'en' ? 'queries' : '查询'}
                    </span>
                    <div className="w-24 h-2 bg-muted-foreground/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${(item?.count / topCountries?.[0]?.count) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Query Analytics Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="BarChart3" size={20} className="text-primary" />
                {language === 'en' ? 'Query Analytics by Period' : '按期间查询分析'}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {language === 'en' ? 'Period' : '期间'}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {language === 'en' ? 'Country' : '国家'}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {language === 'en' ? 'Total Queries' : '总查询数'}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {language === 'en' ? 'Chinese Buyers' : '中国买家'}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {language === 'en' ? 'Completed' : '已完成'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {analytics?.map((row, index) => {
                    const periodStart = row?.[`${period}Start`];
                    const dateStr = periodStart ? new Date(periodStart)?.toLocaleDateString(language === 'en' ? 'en-US' : 'zh-CN') : '-';
                    
                    return (
                      <tr key={index} className="hover:bg-muted/50 transition-smooth">
                        <td className="px-4 py-3 text-sm text-foreground">{dateStr}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{row?.countryInterest || '-'}</td>
                        <td className="px-4 py-3 text-sm text-foreground font-medium">{row?.queryCount}</td>
                        <td className="px-4 py-3 text-sm text-primary font-medium">{row?.chineseBuyerCount}</td>
                        <td className="px-4 py-3 text-sm text-success font-medium">{row?.completedCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Export Button */}
          <div className="flex justify-end">
            <Button variant="outline" iconName="Download" iconPosition="left">
              {language === 'en' ? 'Export Report' : '导出报告'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsTab;